import React, { useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box, AppBar, Toolbar, InputBase, Avatar, IconButton, useMediaQuery, Typography, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import NavSidebar from './components/Chat/NavSidebar';
import ActivityView from './components/Activity/ActivityView';
import ChatLayout from './components/Chat/ChatLayout';
import CommunitiesView from './components/Communities/CommunitiesView';
import CalendarView from './components/Calendar/CalendarView';
import MeetView from './components/Meet/MeetView';
import PeopleView from './components/People/PeopleView';
//import Header from './components/Header';
import Footer from './components/Footer';
import LoginDialog from './components/LoginDialog';

// Simple Error Boundary for Debugging
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) { console.error("Boundary Caught:", error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error">Something went wrong.</Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>{this.state.error?.message}</Typography>
          <Button variant="contained" sx={{ mt: 3 }} onClick={() => window.location.reload()}>Reload App</Button>
        </Box>
      );
    }
    return this.props.children;
  }
}

// Authentic Microsoft Teams Theme
const teamsTheme = createTheme({
  palette: {
    primary: {
      main: '#4b53bc', // Classic Teams Purple
      light: '#6264a7',
      dark: '#3b43a2',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#464775',
    },
    background: {
      default: '#f0f0f0',
      paper: '#ffffff',
    },
    text: {
      primary: '#242424',
      secondary: '#616161',
    },
  },
  typography: {
    fontFamily: '"Segoe UI", "Segoe UI Web (West European)", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
  },
});

function App() {
  const [activeTab, setActiveTab] = useState('teams');
  const [loginOpen, setLoginOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const u = localStorage.getItem('x2infotech_user');
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  React.useEffect(() => {
    const token = localStorage.getItem('x2infotech_token');
    if (!token) setLoginOpen(true);

    const handleForceLogin = () => setLoginOpen(true);
    window.addEventListener('force_login', handleForceLogin);
    return () => window.removeEventListener('force_login', handleForceLogin);
  }, []);

  console.log('App Rendering - Active Tab:', activeTab);

  const handleLoginSuccess = () => {
    window.location.reload();
  };

  return (
    <ErrorBoundary>
      <ThemeProvider theme={teamsTheme}>
        <CssBaseline />

        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: isMobile ? 'column-reverse' : 'row', overflow: 'hidden' }}>
            {/* Left-most slim sidebar or Bottom Nav for mobile */}
            <NavSidebar activeTab={activeTab} onTabChange={setActiveTab} isMobile={isMobile} />

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Top Search Bar - Classic Teams Header */}
              <AppBar
                position="static"
                elevation={0}
                sx={{
                  background: '#4b53bc',
                  height: 48,
                  minHeight: 48,
                  justifyContent: 'center',
                  borderBottom: '1px solid rgba(0,0,0,0.05)'
                }}
              >
                <Toolbar variant="dense" sx={{ justifyContent: 'center', minHeight: 48 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      bgcolor: 'rgba(255,255,255,0.2)',
                      borderRadius: 1,
                      px: 2,
                      width: isMobile ? '90%' : '50%',
                      height: 32,
                    }}
                  >
                    <SearchIcon sx={{ color: '#fff', fontSize: 18, mr: 1 }} />
                    <InputBase
                      placeholder="Search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      sx={{ color: '#fff', flex: 1, fontSize: '14px', '& ::placeholder': { color: 'rgba(255,255,255,0.7)', opacity: 1 } }}
                    />
                  </Box>
                </Toolbar>
              </AppBar>

              {/* Main Content Area */}
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', height: '100%' }}>
                  {activeTab === 'activity' && <ActivityView searchQuery={searchQuery} />}
                  {activeTab === 'chat' && <ChatLayout searchQuery={searchQuery} currentUser={currentUser} />}
                  {activeTab === 'meet' && <MeetView searchQuery={searchQuery} currentUser={currentUser} />}
                  {activeTab === 'people' && (
                    <PeopleView
                      searchQuery={searchQuery}
                      currentUser={currentUser}
                      onStartChat={() => setActiveTab('teams')}
                    />
                  )}
                  {activeTab === 'teams' && <ChatLayout searchQuery={searchQuery} currentUser={currentUser} />}
                  {activeTab === 'calendar' && <CalendarView searchQuery={searchQuery} />}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <LoginDialog
          open={loginOpen}
          onClose={() => setLoginOpen(false)}
          onLoginSuccess={handleLoginSuccess}
        />
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
